import React from 'react'
import { ShiftWithDuty, EventDetailMetaProps, Allocation } from '../data/types'
import { Button } from './ui/Button'
import { ClipboardIcon, EditIcon, DeleteIcon, PlusIcon, DuplicateIcon, EyeIcon, EyeHideIcon } from '../icons'
import { ShiftStatusMap } from '../data'
import { useAuth } from '../components'

type Props = {
    shift: ShiftWithDuty
    meta: EventDetailMetaProps
    formatTime: (timeStr: string | null | undefined) => string
    onEdit?: (shift: ShiftWithDuty) => void
    onDelete?: (shift: ShiftWithDuty) => void
    onAssign?: (shift: ShiftWithDuty) => void
    onView?: (shift: ShiftWithDuty) => void
    onDuplicate?: (shift: ShiftWithDuty) => void
    onCopyAllocations?: (shift: ShiftWithDuty) => void
    canEdit?: boolean
    canDelete?: boolean
    canView?: boolean
    canAssign?: boolean
    canDuplicate?: boolean
    canCopyAllocations?: boolean
    areaShiftsVisible?: boolean
    eventShiftsVisible?: boolean
    showVisibility?: boolean
}


const getStatusBadgeClasses = (status: number) => {
    switch (status) {
        case 0: // In Draft
            return 'bg-gray-300 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
        case 1: // In Review
            return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
        case 2: // Accepted
            return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
        case 3: // Rejected
            return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
        default:
            return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
};


export const ShiftCard = (props: Props) => {
    const { shift, meta, formatTime, onEdit, onDelete, onAssign, onView, onDuplicate, onCopyAllocations, canEdit, canDelete, canView, canAssign, canDuplicate, canCopyAllocations, areaShiftsVisible, eventShiftsVisible, showVisibility } = props

    const { user } = useAuth();

    const canApprove = user.permissions?.includes('approve_shifts');

    // Compute visibility: shift is approved AND (area or event has shifts visible)
    const isApproved = shift.shift_status === 2;
    const isVisibleToVolunteers = isApproved && (areaShiftsVisible || eventShiftsVisible);

    // Determine reason for visibility/hidden status
    const getVisibilityReason = () => {
        if (!isApproved) {
            return 'Shift must be approved to be visible';
        }
        if (!areaShiftsVisible && !eventShiftsVisible) {
            return 'Neither area nor event has shifts visible enabled';
        }
        if (areaShiftsVisible) {
            return 'Visible via area setting';
        }
        return 'Visible via event setting';
    };

    return (
        < div key={shift.id} className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg" style={{ borderColor: shift.access_color?.color, borderWidth: 5 }} >
            <div className="flex justify-between items-start mb-2">
                <h5 className="font-semibold text-gray-900 dark:text-gray-100">
                    {shift.title ?? shift.duty_name}
                </h5>

                <div className='flex items-center'>
                    {/* Visibility to volunteers indicator - only shown to admins */}
                    {showVisibility && (
                        <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium me-2 ${isVisibleToVolunteers
                                ? 'bg-success-100 text-success-700 dark:bg-success-900/50 dark:text-success-300'
                                : 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-400'
                                }`}
                            title={getVisibilityReason()}
                        >
                            {isVisibleToVolunteers ? (
                                <><EyeIcon className="w-3 h-3" /></>
                            ) : (
                                <><EyeHideIcon className="w-3 h-3" /></>
                            )}
                        </span>
                    )}

                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium me-3 ${getStatusBadgeClasses(shift.shift_status)}`}>
                        {ShiftStatusMap[shift.shift_status]}
                    </span>

                    {(canEdit || canDelete || canDuplicate || canCopyAllocations) && (
                        <div className="flex gap-1">
                            {canEdit && onEdit && (
                                <Button onClick={() => onEdit(shift)} variant="icon" size="sm" title="Edit shift">
                                    <EditIcon className="w-4 h-4" />
                                </Button>
                            )}
                            {canDuplicate && onDuplicate && (
                                <Button onClick={() => onDuplicate(shift)} variant="icon" color="primary" size="sm" title="Duplicate shift">
                                    <DuplicateIcon className="w-4 h-4" />
                                </Button>
                            )}
                            {canCopyAllocations && onCopyAllocations && (
                                <Button onClick={() => onCopyAllocations(shift)} variant="icon" color="primary" size="sm" title={meta.copyShiftAllocationsButton}>
                                    <ClipboardIcon className="w-4 h-4" />
                                </Button>
                            )}
                            {canDelete && onDelete && (
                                <Button onClick={() => onDelete(shift)} variant="icon" color="danger" size="sm" title="Delete shift">
                                    <DeleteIcon className="w-4 h-4" />
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className='mb-4'>
                <p className="text-gray-600 dark:text-gray-400">
                    <span className="font-semibold">{"Duty"}:</span>{" "}
                    {shift.duty_name}
                </p>
            </div>

            {/* Lead Details */}

            <div className="flex flex-wrap items-start gap-x-6 gap-y-1 text-sm">


                <p className="text-gray-600 dark:text-gray-400">
                    <span className="font-semibold">{meta.leadNameTitle}:</span>{" "}
                    {shift.lead ? `${shift.lead.first_name} ${shift.lead.last_name}` : "Unassigned"}
                </p>

                <p className="text-gray-600 dark:text-gray-400">
                    <span className="font-semibold">{meta.contactTitle}:</span>{" "}
                    {shift.lead ? shift.lead.phone : "Unavailable"}
                </p>
            </div>

            <div className="flex flex-wrap items-start gap-x-6 gap-y-1 text-sm">
                <p className="text-gray-600 dark:text-gray-400">
                    <span className="font-semibold">{meta.deputyLeadNameTitle}:</span>{" "}
                    {shift.deputy_lead ? `${shift.deputy_lead.first_name} ${shift.deputy_lead.last_name}` : "Unassigned"}
                </p>

                <p className="text-gray-600 dark:text-gray-400">
                    <span className="font-semibold">{meta.contactTitle}:</span>{" "}
                    {shift.deputy_lead ? shift.deputy_lead.phone : "Unavailable"}
                </p>
            </div>

            <hr className="my-2 border-t border-gray-400/20 dark:border-gray-200/10" />

            {/* Start Time and End Time*/}

            <div className="space-y-1 text-sm flex justify-between">
                <p className="text-gray-600 dark:text-gray-400">
                    <span className="font-semibold">{meta.startTimeLabel}:</span> {formatTime(shift.start_time)}
                </p>
                <p className="text-gray-600 dark:text-gray-400">
                    <span className="font-semibold">{meta.endTimeLabel}:</span> {formatTime(shift.end_time)}
                </p>
            </div>

            <hr className="my-2 border-t border-gray-400/20 dark:border-gray-200/10" />
            <div className="mb-6">
                <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                    <span className="font-semibold">{"Criteria"}:</span>{" "}
                    {shift.criteria ?? "No shift criteria available."}
                </p>
            </div>

            <hr className="my-2 border-t border-gray-400/20 dark:border-gray-200/10" />
            <div className="mb-6">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-semibold">{"Instructions"}:</span>{" "}
                    {shift.instruction ?? "No shift instructions available."}
                </p>
            </div>

            <hr className="my-2 border-t border-gray-400/20 dark:border-gray-200/10" />

            {/* Allocation Status Counts*/}

            <div className='flex justify-between text-sm mb-2'>
                <p className="text-gray-600 dark:text-gray-400">
                    <span className="font-semibold">{meta.required_volunteer_count_label}:</span>{" "}
                    {shift.required_volunteer_count ? shift.required_volunteer_count : '?'}
                </p>

                <p className="text-gray-600 dark:text-gray-400">
                    <span className="font-semibold">{meta.allocation_count_label}:</span>{" "}
                    {shift.allocation_count || 0}
                </p>
            </div>

            <hr className="my-2 border-t border-gray-400/20 dark:border-gray-200/10" />

            <div className='flex justify-between mb-8 text-sm mt-4'>
                <p className="text-gray-600 dark:text-gray-400">
                    <span className="font-semibold">{meta.accepted_allocations_label}:</span>{" "}
                    {shift.accepted_count}
                </p>

                <p className="text-gray-600 dark:text-gray-400">
                    <span className="font-semibold">{meta.rejected_allocations_label}:</span>{" "}
                    {shift.rejected_count}
                </p>

                <p className="text-gray-600 dark:text-gray-400">
                    <span className="font-semibold">{meta.pending_allocations_label}:</span>{" "}
                    {shift.pending_count}
                </p>

                <p className="text-gray-600 dark:text-gray-400">
                    <span className="font-semibold">{meta.leads_count_label}:</span>{" "}
                    {shift.leads_count || 0}
                </p>
            </div>


            {(canAssign || canView) && (onAssign || onView) && (
                <div className='flex justify-end'>
                    {canAssign && onAssign ? (
                        <Button size='sm' color='success' onClick={() => { onAssign(shift) }}>
                            {shift?.shift_status === 0 || canApprove ? "Manage " : "View "} Allocations
                        </Button>
                    ) : canView && onView ? (
                        <Button size='sm' color='primary' onClick={() => { onView(shift) }}>
                            View Allocations
                        </Button>
                    ) : null}
                </div>
            )}


        </div >
    )
}
